import { useEffect, useMemo, useRef, useState } from 'react'
import { IconTrash } from '@tabler/icons-react'
import { UUIDTypes, v4 as uuidv4 } from 'uuid'
import { z } from 'zod'
import {
  Button,
  Group,
  Flex,
  Input,
  Modal,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import {
  SpeciData,
  SpeciDataLinesMap,
  SpeciLine,
  SpeciLinesMap,
  SpeciStation,
  SpeciStationDetails,
} from '@shared/components/speci/speci-types'
import { SpeciLineEditor } from '@shared/components/speci/speci-line-editor/speci-line-editor'
import { SpeciLineWarnings } from '@shared/components/speci/speci-line-warnings/speci-line-warnings'
import {
  SpeciLineAction,
  SpeciLinesTable,
} from '@shared/components/speci/speci-lines-table/speci-lines-table'

import { SPECI_CONSIGNMENT_STATUS_COMBOBOX_OPTIONS } from '@shared/constants/speci-consignment-status'
import { SPECI_BREED_LABELS } from '@shared/constants/breeds'
import { WOOLSTORE_LABELS } from '@shared/constants/woolstores'
import { getSpeciLineInformation } from '@shared/components/speci/speci-helpers/speci-helpers'
import { PillAlert } from '@shared/components/pill-alert/pill-alert'

export function getContractOptions(
  currentSeasonContracts: { recordNumber?: string; contractName?: string | null }[] | undefined
): string[] | undefined {
  // Do we have any record numbers on the contracts?
  // If so we want to display the option as <record number> - <Name>
  if (currentSeasonContracts?.some((contract) => contract.recordNumber)) {
    return currentSeasonContracts
      ?.map((contract) =>
        contract.contractName
          ? `${contract.recordNumber} - ${contract.contractName}`
          : contract.recordNumber
      )
      .filter((option): option is string => option !== undefined)
  }

  // Otherwise, we just display the name and we remove blanks (null or undefined)
  return currentSeasonContracts
    ?.map((contract) => contract.contractName)
    .filter(
      (contractName): contractName is string => contractName !== undefined && contractName !== null
    )
}

export enum SpeciAction {
  NEW = 'new',
  EDIT = 'edit',
}

export type SpeciEditorSubmitModalProps = {
  isModalOpen: boolean
  closeModal: () => void
}

export type SpeciEditorProps = {
  action: SpeciAction
  station: SpeciStation
  stationDetailsForSpeci: SpeciStationDetails | undefined
  stationDataLoading: boolean
  speciId: string
  speciData: SpeciData | undefined
  speciDataIsStale: boolean
  speciDataIsFetching: boolean
  onUpdateSpeci: (
    speci: SpeciData,
    stationRecordNumber: string,
    submitPending: boolean
  ) => Promise<void>
  onNavigateToSpeciHomepage: () => void
  submitTitle?: string
  renderBackButton?: boolean
  useInternalInstructions?: boolean
  requireInstructions?: boolean
  renderSubmitModal: (modalProps: SpeciEditorSubmitModalProps) => React.ReactNode
  disableDuplicateLine?: boolean
  displayCertificationStatus?: boolean
}

export function SpeciEditor({
  action = SpeciAction.NEW,
  station,
  stationDetailsForSpeci,
  stationDataLoading,
  speciId,
  speciData,
  speciDataIsStale: isStale,
  speciDataIsFetching: isFetching,
  onUpdateSpeci,
  onNavigateToSpeciHomepage,
  submitTitle = 'Submit',
  renderBackButton = false,
  useInternalInstructions = false,
  requireInstructions = false,
  renderSubmitModal,
  disableDuplicateLine = false,
  displayCertificationStatus = false,
}: SpeciEditorProps) {
  const [savePending, setSavePending] = useState(false)
  const [submitPending, setSubmitPending] = useState(false)
  const [lineToEdit, setLineToEdit] = useState<SpeciLine | undefined>(undefined)
  const [lineToDelete, setLineToDelete] = useState<SpeciLine | undefined>(undefined)
  const [lineToBeDuplicated, setLineToBeDuplicated] = useState<UUIDTypes | undefined>(undefined)
  const [deleteModalState, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false)
  const [submitModalState, { open: openSubmitModal, close: closeSubmitModal }] =
    useDisclosure(false)
  const [sumOfTotalBales, setSumOfTotalBales] = useState(0)

  const lineEditorFirstFieldRef = useRef<HTMLInputElement>(null)

  const contractOptions: string[] | undefined = useMemo(
    () => getContractOptions(stationDetailsForSpeci?.currentSeasonContracts),
    [stationDetailsForSpeci?.currentSeasonContracts]
  )

  /**
   * Transforms the form data (SpeciDataLinesMap) into the DTO format expected by the API (SpeciData)
   */
  const transformFormDataToDto = (formData: SpeciDataLinesMap): SpeciData => {
    return {
      ...formData,
      lines: Object.values(formData.lines),
    }
  }

  const handleUpdateSpeci = async (formData: SpeciDataLinesMap, submitPendingValue: boolean) => {
    setSubmitPending(submitPendingValue)
    const speciDto = transformFormDataToDto(formData)
    await onUpdateSpeci(speciDto, station.recordNumber, submitPendingValue)
  }

  /**
   * Defines the schema for the form used in the Speci Editor component.
   *
   * The schema validates the following fields:
   *
   * - `classer`: An optional string representing the classer.
   * - `classerComment`: An optional string with a maximum length of 500 characters.
   * - `woolStore`: A required non-empty string.
   * - `breeds`: A required array of strings. At least one breed is required.
   * - `consignmentStatus`: A nullable string that must not be null.
   * - `lines`: A record of key-value pairs where:
   *   - At least one line is required.
   *   - If `requireInstructions` is true, all lines must have a non-empty `instructions` string.
   */
  const formSchema = z.object({
    classer: z.string().optional(),
    classerComment: z
      .string()
      .max(500, { message: 'Classer comment must be less than 500 characters' })
      .optional(),
    woolStore: z.string().nonempty({ message: 'Wool store is required' }),
    breeds: z.array(z.string()).nonempty({ message: 'At least one breed is required' }),
    consignmentStatus: z
      .string()
      .nullable()
      .refine((value) => value !== null, {
        message: 'Consignment status is required',
      }),
    lines: z
      .record(z.any())
      .refine((lines) => Object.keys(lines).length > 0, {
        message: 'At least one line is required',
      })
      .refine(
        (lines) => {
          if (requireInstructions) {
            return Object.values(lines as Record<string, SpeciLine>).every(
              (line) => typeof line.instructions === 'string' && line.instructions.length > 0
            )
          }
          return true
        },
        {
          message: 'All lines must have an instruction',
        }
      ),
  })

  const form = useForm<SpeciDataLinesMap>({
    mode: 'uncontrolled',
    initialValues: {
      id: speciId,
      classer: '',
      classerComment: '',
      woolStore: '',
      breeds: [],
      consignmentStatus: null,
      lines: {},
    },
    validate: zodResolver(formSchema),
    transformValues: (values): any => ({
      ...values,
      lines: getLinesFromSpeciMap(),
    }),
    enhanceGetInputProps: (payload) => {
      if (action === SpeciAction.EDIT && !payload.form.initialized) {
        return { disabled: true }
      }
      return {}
    },
  })

  /**
   * Loads the given specification data into the form and initializes it.
   *
   * @param {SpeciData} speci - The specification data transfer object containing the details to be loaded into the form.
   * @returns {void}
   */
  const loadSpeciIntoForm = (speci: SpeciData) => {
    const transformNullToEmptyString = (value: any) => (value === null ? '' : value)

    const transformedSpeci = {
      ...speci,
      classer: transformNullToEmptyString(speci.classer),
      classerComment: transformNullToEmptyString(speci.classerComment),
      woolStore: transformNullToEmptyString(speci.woolStore),
    }

    const speciLinesMap = transformedSpeci.lines.reduce((acc, line) => {
      acc[line.id.toString()] = line as SpeciLine
      return acc
    }, {} as SpeciLinesMap)

    form.initialize({
      ...transformedSpeci,
      lines: speciLinesMap,
    })
    setSumOfTotalBales(getTotalBales())
  }

  useEffect(() => {
    const speciDataHasBeenFetched = isStale && !isFetching
    if (speciData && speciDataHasBeenFetched) {
      loadSpeciIntoForm(speciData)
    }
  }, [speciData, isStale, isFetching])

  const getTotalBales = (): number =>
    getLinesFromSpeciMap().reduce((sum, line) => sum + line.baleNumbers.length, 0)

  /**
   * Retrieves an array of `SpeciWoolLineData` objects from the form's values.
   *
   * @returns {SpeciLine[]} An array of `SpeciWoolLineData` objects extracted from the form's lines.
   */
  const getLinesFromSpeciMap = (): SpeciLine[] => Object.values(form.getValues().lines)

  const updateLinesInFormState = (lineData: SpeciLine) => {
    form.setFieldValue(`lines.${lineData.id}`, lineData)
    setSumOfTotalBales(getTotalBales())
  }

  /**
   * Adds a new line to the wool specification and updates the specification.
   *
   * This function sets an ID for the new line if it doesn't already have one,
   * updates the form state with the new line data, and then triggers a mutation
   * to update the specification. If the mutation fails, it reverts the form
   * values to their previous state.
   *
   * @param {SpeciLine} lineData - The data for the new wool line to be added.
   */
  const addLineAndUpdateSpeci = (lineData: SpeciLine) => {
    lineData.id = lineData.id || uuidv4()
    const previousFormValues = form.getValues()
    updateLinesInFormState(lineData)
    resetCurrentlyEditingLine()

    handleUpdateSpeci(form.getTransformedValues(), false).catch(() => {
      form.setValues(previousFormValues)
    })
  }

  /**
   * Saves the current wool specification for later.
   *
   * This function checks if there's a pending update. If not,
   * it sets the savePending state to true and then calls handleUpdateSpeci
   * with the transformed form values.
   *
   * Upon successful update, it navigates to the wool specification homepage.
   *
   * @returns {void}
   */
  const saveSpeciForLater = async () => {
    if (savePending) return
    setSavePending(true)
    try {
      await handleUpdateSpeci(form.getTransformedValues(), false)
      onNavigateToSpeciHomepage()
    } finally {
      setSavePending(false)
    }
  }

  /**
   * Updates a specific line based on the provided action.
   *
   * @param {UUIDTypes | undefined} lineId - The unique identifier of the line to be updated.
   * @param {SpeciLineAction} speciLineAction - The action to be performed on the line (EDIT, DUPLICATE or DELETE).
   *
   * @remarks
   * - If the action is EDIT, the line is deep cloned to avoid mutating the state in the table and set for editing.
   * - If the action is DUPLICATE, the line is cloned, its ID is unset to create a new line, and it is set for editing.
   * - If the action is DELETE, the line is set for deletion and the delete modal is opened.
   */
  const updateSpeciLine = (lineId: UUIDTypes | undefined, speciLineAction: SpeciLineAction) => {
    if (speciLineAction === SpeciLineAction.EDIT || speciLineAction === SpeciLineAction.DUPLICATE) {
      resetCurrentlyEditingLine()
      const line = form.getValues().lines[lineId as string]
      if (!line) return
      const lineClone = JSON.parse(JSON.stringify(line))

      // If duplicating line, unset the ID to create a new line
      if (speciLineAction === SpeciLineAction.DUPLICATE) {
        setLineToBeDuplicated(lineClone.id)
        lineClone.id = undefined
      }

      setLineToEdit(lineClone)
    } else if (speciLineAction === SpeciLineAction.DELETE) {
      setLineToDelete(form.getValues().lines[lineId as string])
      openDeleteModal()
    }
  }

  const resetCurrentlyEditingLine = () => {
    setLineToEdit(undefined)
    setLineToBeDuplicated(undefined)
  }

  /**
   * Confirms the deletion of a line item from the form.
   *
   * This function performs the following actions:
   * 1. Checks if there is a line to delete. If not, it returns early.
   * 2. Retrieves the current form values and creates a deep copy of them.
   * 3. Removes the line from the form values and updates the form state.
   * 4. Clears the deletion state and closes the delete confirmation modal.
   * 5. If the line being deleted is currently being edited, it clears the edit state.
   * 6. Attempts to update the specification via a mutation. If the mutation fails, it restores the previous form values.
   *
   * @returns {void}
   */
  const confirmDeleteLine = () => {
    if (!lineToDelete) return

    const formValues = form.getValues()
    const previousFormValues = JSON.parse(JSON.stringify(formValues))
    delete formValues.lines[lineToDelete.id as string]
    form.setValues(formValues)
    setLineToDelete(undefined)
    closeDeleteModal()

    if (lineToDelete.id === lineToEdit?.id) {
      resetCurrentlyEditingLine()
    }

    handleUpdateSpeci(form.getTransformedValues(), false).catch(() => {
      form.setValues(previousFormValues)
    })
  }

  const cancelDeleteLine = () => {
    setLineToDelete(undefined)
    closeDeleteModal()
  }

  const askForSubmitConfirmation = () => {
    handleUpdateSpeci(form.getTransformedValues(), true)
      .then(() => openSubmitModal())
      .catch(() => setSubmitPending(false))
  }

  const cancelSubmitSpeci = () => {
    setSubmitPending(false)
    closeSubmitModal()
  }

  const speciLines = getLinesFromSpeciMap()
  const speciLinesInformation = getSpeciLineInformation(speciLines)

  return (
    <>
      <Stack>
        <Title order={2}>{action === SpeciAction.NEW ? 'Create' : 'Edit'} Wool Specification</Title>
        <Text>
          Please enter the bale numbers and line description for each line. The line number and
          traditional description are optional.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Stack gap='xs'>
            <Input.Label>Brand</Input.Label>
            <Text>{station?.brand || 'N/A'}</Text>
          </Stack>
          <Stack gap='xs'>
            <Input.Label>Station ID</Input.Label>
            <Text>{station.recordNumber}</Text>
          </Stack>
          <Stack gap='xs'>
            <Input.Label>Trading Name</Input.Label>
            <Text>{station?.tradingName || 'N/A'}</Text>
          </Stack>
          {displayCertificationStatus && (
            <>
              <Stack gap='xs'>
                <Input.Label>ZQ Status</Input.Label>
                {stationDetailsForSpeci?.isRws ? (
                  <Text>{stationDetailsForSpeci?.zqStatus ?? ''}</Text>
                ) : (
                  <PillAlert text={stationDetailsForSpeci?.zqStatus ?? ''} />
                )}
              </Stack>
              <Stack gap='xs'>
                <Input.Label>ZQRX Status</Input.Label>
                <Text>{stationDetailsForSpeci?.zqrxStatus ?? ''}</Text>
              </Stack>
              <Stack gap='xs'>
                <Input.Label>RWS Status</Input.Label>
                {stationDetailsForSpeci?.isRws ? (
                  <Text>{stationDetailsForSpeci?.rwsStatus ?? ''}</Text>
                ) : (
                  <PillAlert text={stationDetailsForSpeci?.rwsStatus ?? ''} />
                )}
              </Stack>
            </>
          )}
        </SimpleGrid>
        <SpeciLineEditor
          initialData={lineToEdit}
          contractOptions={contractOptions}
          contractsIsLoading={stationDataLoading}
          useInternalInstructions={useInternalInstructions}
          requireInstructions={requireInstructions}
          firstFieldRef={lineEditorFirstFieldRef}
          onSubmit={addLineAndUpdateSpeci}
          onCancel={resetCurrentlyEditingLine}
        />
        <Title order={2} size='h3'>
          Current lines (i.e. in progress)
        </Title>
        {form?.errors?.lines && (
          <Text size='xs' c='red'>
            {form?.errors?.lines}
          </Text>
        )}
        <SpeciLinesTable
          data={speciLines}
          activeRowId={lineToEdit?.id || lineToBeDuplicated}
          isDuplicating={!!lineToBeDuplicated}
          sumOfTotalBales={sumOfTotalBales}
          updateSpeciLine={updateSpeciLine}
          disableDuplicate={disableDuplicateLine}
          lineEditorFirstFieldRef={lineEditorFirstFieldRef}
          speciLinesInformation={speciLinesInformation}
        />
        <SpeciLineWarnings
          speciLineCount={speciLines.length}
          speciLinesInformation={speciLinesInformation}
        />
        <form onSubmit={form.onSubmit(() => askForSubmitConfirmation())} data-testid='speci-form'>
          <Stack>
            <SimpleGrid cols={{ base: 1, lg: 3 }}>
              <TextInput
                label='Classer'
                placeholder='Optional'
                key={form.key('classer')}
                {...form.getInputProps('classer')}
              />
              <Select
                label='Wool store'
                placeholder='Select woolstore'
                data={WOOLSTORE_LABELS}
                allowDeselect={false}
                key={form.key('woolStore')}
                {...form.getInputProps('woolStore')}
              />
              <MultiSelect
                name='breeds'
                label='Breed(s)'
                placeholder='Select breed(s)'
                data={SPECI_BREED_LABELS}
                key={form.key('breeds')}
                {...form.getInputProps('breeds')}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, lg: 2 }}>
              <Textarea
                label='Classer comment'
                placeholder='Optional'
                autosize
                key={form.key('classerComment')}
                {...form.getInputProps('classerComment')}
              />
              <Select
                label='Consignment status'
                placeholder='Select consignment status'
                data={SPECI_CONSIGNMENT_STATUS_COMBOBOX_OPTIONS}
                allowDeselect={false}
                key={form.key('consignmentStatus')}
                {...form.getInputProps('consignmentStatus')}
              />
            </SimpleGrid>
            <Group justify='flex-end' mt='md' gap='xs'>
              {renderBackButton && (
                <Button variant='outline' loading={savePending} onClick={onNavigateToSpeciHomepage}>
                  Back
                </Button>
              )}
              <Button variant='outline' loading={savePending} onClick={() => saveSpeciForLater()}>
                Save for later
              </Button>
              <Button type='submit' loading={submitPending}>
                {submitTitle}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>

      <Modal
        data-testid='delete-speci-modal'
        title={
          <Flex justify='flex-start' align='center'>
            <IconTrash size={28} color='var(--mantine-color-gray-4)' />
            <Text fw={500} size='lg' ml='sm'>
              Delete line
            </Text>
          </Flex>
        }
        opened={deleteModalState}
        onClose={closeDeleteModal}
        centered
        closeButtonProps={{ 'aria-label': 'Close line deletion modal' }}
        returnFocus={false}
      >
        Are you sure you want to delete the line with description {lineToDelete?.description}?
        <Group justify='flex-end' mt='md' gap='xs'>
          <Button variant='transparent' onClick={cancelDeleteLine}>
            No
          </Button>
          <Button color='red' onClick={confirmDeleteLine}>
            Yes, delete this line
          </Button>
        </Group>
      </Modal>

      {renderSubmitModal({
        isModalOpen: submitModalState,
        closeModal: cancelSubmitSpeci,
      })}
    </>
  )
}
